import React, { useState, useEffect, useRef } from 'react';
import { supabase } from '../../../lib/supabase';
import { useAuth } from '../../../contexts/AuthContext';
import Icon from '../../../components/AppIcon';

const RecentInspectionsDropdown = ({ onSelectInspection, onNewRevision, currentWorkOrderId }) => {
  const { userProfile } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const [recentInspections, setRecentInspections] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef?.current && !dropdownRef?.current?.contains(e?.target)) {
        setIsOpen(false);
      }
    };
    document?.addEventListener('mousedown', handleClickOutside);
    return () => document?.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const fetchRecentInspections = async () => {
    if (!userProfile?.company_id) return;
    setIsLoading(true);
    try {
      const { data, error } = await supabase
        ?.from('technical_inspections')
        ?.select(`
          id,
          work_order_id,
          category,
          status,
          created_at,
          work_order:work_orders!inner(
            id,
            folio_ot,
            estado_ot,
            company_id,
            client:clients(nombre),
            vehicle:vehicles(marca, modelo, year, placas, vin)
          )
        `)
        ?.eq('work_order.company_id', userProfile?.company_id)
        ?.order('created_at', { ascending: false })
        ?.limit(100);

      if (error) {
        console.warn('[RecentInspectionsDropdown] Error:', error?.message);
        setRecentInspections([]);
        return;
      }

      // Deduplicate by work_order_id, keep most recent
      const seen = new Set();
      const unique = [];
      for (const row of (data || [])) {
        const woId = row?.work_order_id;
        if (!seen?.has(woId)) {
          seen?.add(woId);
          unique?.push(row);
        }
      }

      setRecentInspections(unique?.slice(0, 20));
    } catch (err) {
      console.error('[RecentInspectionsDropdown] Fetch error:', err);
      setRecentInspections([]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleToggle = () => {
    if (!isOpen) {
      fetchRecentInspections();
    }
    setIsOpen(!isOpen);
  };

  const handleSelect = (inspection) => {
    setIsOpen(false);
    onSelectInspection?.(inspection);
  };

  const handleNewRevision = (inspection) => {
    setIsOpen(false);
    onNewRevision?.(inspection);
  };

  const handleDelete = async (inspection, e) => {
    e?.stopPropagation();
    const woId = inspection?.work_order_id;
    const folio = inspection?.work_order?.folio_ot || woId;
    if (!window.confirm(`¿Eliminar todas las inspecciones de la OT ${folio}? Esta acción no se puede deshacer.`)) return;

    setDeletingId(woId);
    try {
      const { error } = await supabase
        ?.from('technical_inspections')
        ?.delete()
        ?.eq('work_order_id', woId);

      if (error) {
        console.error('[RecentInspectionsDropdown] Delete error:', error?.message);
        alert('Error al eliminar la inspección: ' + error?.message);
        return;
      }

      // Remove from local list
      setRecentInspections(prev => prev?.filter(i => i?.work_order_id !== woId));
    } catch (err) {
      console.error('[RecentInspectionsDropdown] Delete exception:', err);
      alert('Error inesperado al eliminar.');
    } finally {
      setDeletingId(null);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    try {
      return new Date(dateStr)?.toLocaleDateString('es-MX', {
        day: '2-digit', month: 'short', year: 'numeric'
      });
    } catch {
      return dateStr;
    }
  };

  const getStatusBadge = (status) => {
    const map = {
      bueno: { label: 'Bueno', cls: 'bg-success/10 text-success' },
      requiere_atencion: { label: 'Req. Atención', cls: 'bg-warning/10 text-warning' },
      critico: { label: 'Crítico', cls: 'bg-destructive/10 text-destructive' },
      malo: { label: 'Malo', cls: 'bg-destructive/10 text-destructive' },
      regular: { label: 'Regular', cls: 'bg-warning/10 text-warning' },
      no_aplica: { label: 'N/A', cls: 'bg-muted text-muted-foreground' },
    };
    return map?.[status] || { label: status, cls: 'bg-muted text-muted-foreground' };
  };

  return (
    <div ref={dropdownRef} className="relative">
      <button
        type="button"
        onClick={handleToggle}
        className="flex items-center gap-2 px-4 py-2 rounded-lg border border-border bg-card hover:bg-muted transition-colors text-sm font-medium text-foreground"
      >
        <Icon name="History" size={16} className="text-muted-foreground" />
        <span>Inspecciones Guardadas</span>
        <Icon name={isOpen ? 'ChevronUp' : 'ChevronDown'} size={14} className="text-muted-foreground" />
      </button>

      {isOpen && (
        <div className="absolute left-0 top-full mt-1 z-50 w-[580px] max-w-[95vw] bg-card border border-border rounded-lg shadow-lg">
          <div className="flex items-center justify-between px-4 py-3 border-b border-border">
            <h3 className="text-sm font-semibold text-foreground flex items-center gap-2">
              <Icon name="ClipboardList" size={16} className="text-primary" />
              Inspecciones Guardadas (últimas 20)
            </h3>
            {isLoading && (
              <div className="w-4 h-4 border-2 border-primary border-t-transparent rounded-full animate-spin" />
            )}
          </div>

          <div className="max-h-96 overflow-y-auto">
            {!isLoading && recentInspections?.length === 0 && (
              <div className="px-4 py-6 text-center text-sm text-muted-foreground">
                <Icon name="ClipboardX" size={24} className="mx-auto mb-2 opacity-40" />
                No hay inspecciones guardadas
              </div>
            )}

            {recentInspections?.map((inspection) => {
              const wo = inspection?.work_order;
              const vehicle = wo?.vehicle;
              const client = wo?.client;
              const statusBadge = getStatusBadge(inspection?.status);
              const isCurrent = wo?.id === currentWorkOrderId;
              const isDeleting = deletingId === inspection?.work_order_id;

              return (
                <div
                  key={inspection?.id}
                  className={`flex items-start gap-3 px-4 py-3 border-b border-border/50 last:border-0 transition-colors ${
                    isCurrent ? 'bg-primary/5' : 'hover:bg-muted/30'
                  } ${isDeleting ? 'opacity-50' : ''}`}
                >
                  {/* Vehicle icon */}
                  <div className="shrink-0 w-8 h-8 rounded-full bg-muted flex items-center justify-center mt-0.5">
                    <Icon name="Car" size={15} className="text-muted-foreground" />
                  </div>

                  <div className="flex-1 min-w-0">
                    {/* Row 1: Marca Modelo Año + current badge */}
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-sm font-semibold text-foreground">
                        {vehicle?.marca || '—'} {vehicle?.modelo || ''} {vehicle?.year || ''}
                      </span>
                      {isCurrent && (
                        <span className="text-xs px-1.5 py-0.5 rounded bg-primary/10 text-primary">Actual</span>
                      )}
                      <span className={`text-xs px-1.5 py-0.5 rounded font-medium ${statusBadge?.cls}`}>
                        {statusBadge?.label}
                      </span>
                    </div>

                    {/* Row 2: VIN + Placas */}
                    <div className="flex items-center gap-3 mt-0.5 flex-wrap">
                      {vehicle?.vin && (
                        <span className="text-xs text-muted-foreground font-mono">
                          <Icon name="Hash" size={10} className="inline mr-0.5" />
                          VIN: {vehicle?.vin}
                        </span>
                      )}
                      {vehicle?.placas && (
                        <span className="text-xs text-muted-foreground font-mono">
                          <Icon name="CreditCard" size={10} className="inline mr-0.5" />
                          Placas: {vehicle?.placas}
                        </span>
                      )}
                    </div>

                    {/* Row 3: Folio + Cliente + Fecha */}
                    <div className="flex items-center gap-3 mt-0.5 flex-wrap">
                      <span className="text-xs text-muted-foreground font-mono">{wo?.folio_ot}</span>
                      {client?.nombre && (
                        <span className="text-xs text-muted-foreground">
                          <Icon name="User" size={10} className="inline mr-0.5" />
                          {client?.nombre}
                        </span>
                      )}
                      <span className="text-xs text-muted-foreground">
                        <Icon name="Calendar" size={10} className="inline mr-0.5" />
                        {formatDate(inspection?.created_at)}
                      </span>
                    </div>
                  </div>

                  {/* Action buttons */}
                  <div className="flex items-center gap-1 shrink-0">
                    <button
                      type="button"
                      onClick={() => handleSelect(inspection)}
                      className="px-2.5 py-1.5 text-xs rounded-md bg-muted hover:bg-muted/80 text-foreground transition-colors"
                      title="Ver inspección"
                      disabled={isDeleting}
                    >
                      <Icon name="Eye" size={13} />
                    </button>
                    <button
                      type="button"
                      onClick={() => handleNewRevision(inspection)}
                      className="px-2.5 py-1.5 text-xs rounded-md bg-primary/10 hover:bg-primary/20 text-primary transition-colors font-medium"
                      title="Nueva revisión para este vehículo"
                      disabled={isDeleting}
                    >
                      <Icon name="Plus" size={13} />
                    </button>
                    <button
                      type="button"
                      onClick={(e) => handleDelete(inspection, e)}
                      className="px-2.5 py-1.5 text-xs rounded-md bg-destructive/10 hover:bg-destructive/20 text-destructive transition-colors"
                      title="Eliminar inspección"
                      disabled={isDeleting}
                    >
                      {isDeleting
                        ? <div className="w-3 h-3 border-2 border-destructive border-t-transparent rounded-full animate-spin" />
                        : <Icon name="Trash2" size={13} />
                      }
                    </button>
                  </div>
                </div>
              );
            })}
          </div>

          <div className="px-4 py-3 border-t border-border bg-muted/30">
            <p className="text-xs text-muted-foreground">
              <Icon name="Info" size={11} className="inline mr-1" />
              <Icon name="Eye" size={11} className="inline" /> Ver · <Icon name="Plus" size={11} className="inline" /> Nueva revisión · <Icon name="Trash2" size={11} className="inline" /> Eliminar inspección
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default RecentInspectionsDropdown;
